import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react"; // Import icons from Lucide
import LoadingButton from "../../components/common/LoadingButton";
import { useSelector } from "react-redux";
import AppStore from "../../config/AppStore";
import {
  sendRequestToFetchUserContactDetails,
  sendRequestToResetPassword
} from "../../view_logics/ContactInfo";

const ChangePassword = () => {
  let store = AppStore.getAppStoreInstance();
  let accessToken = store.getState().login.accessToken;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userById, setUserById] = useState(null);
  const [showOldPassword, setShowOldPassword] = useState(false); // State for toggling visibility
  const [response, setResponse] = useState(null);

  const { userId, publisherId } = useSelector((state) => state?.login);

  useEffect(() => {
    if (publisherId) {
      const fetchData = async () => {
        let res = await sendRequestToFetchUserContactDetails({
          id: publisherId,
          accessToken: accessToken,
        });
        if (res) {
          setUserById({ ...res });
        }
      };
      fetchData();
    }
  }, [publisherId,response]);

  const handleResetPasswordSubmitHandler = async (values, resetForm, setLoading) => {
    setLoading(true);
    console.log("Password Change Data:", values);

    const serializedData = {
      publisher_id: publisherId,
      password: values.newPassword,
      old_password: values.oldPassword,
    }

    const inputObj = {
      accessToken: accessToken,
      url: "/reset-password",
      history: navigate,
      dataToPost: serializedData,
      loadingHandler: setLoading,
      setResponseHandler: setResponse
    };

    sendRequestToResetPassword(inputObj);

    // You can replace this with your actual API call
    setTimeout(() => {
      setLoading(false);
      resetForm();
      // navigate("/profile"); // handle navigation outside if needed
    }, 2000);
  };

  const formik = useFormik({
    initialValues: {
      publisherId: publisherId || "",
      oldPassword: userById?.password || "",
      newPassword: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Old password is required"),
      newPassword: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("New password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      await handleResetPasswordSubmitHandler(values, resetForm, setLoading);
    },
  });

  return (
    <div className="container mt-4 d-flex justify-content-center">
      <div className="w-75">
        <h2>Change Password</h2>
        <form onSubmit={formik.handleSubmit}>
          {/* Old Password Field */}
          <div className="mb-3 position-relative">
            <label className="form-label">Old Password</label>
            <input
              type="hidden"
              name="publisherId"
              {...formik.getFieldProps("publisherId")}
            />
            <div className="input-group">
              <input
                type={showOldPassword ? "text" : "password"}
                readOnly
                className="form-control"
                {...formik.getFieldProps("oldPassword")}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowOldPassword(!showOldPassword)}
              >
                {showOldPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {formik.touched.oldPassword && formik.errors.oldPassword && (
              <div className="text-danger">{formik.errors.oldPassword}</div>
            )}
          </div>

          {/* New Password Field */}
          <div className="mb-3">
            <label className="form-label">New Password</label>
            <input
              type="password"
              className="form-control"
              {...formik.getFieldProps("newPassword")}
            />
            {formik.touched.newPassword && formik.errors.newPassword && (
              <div className="text-danger">{formik.errors.newPassword}</div>
            )}
          </div>

          {/* Confirm New Password Field */}
          <div className="mb-3">
            <label className="form-label">Confirm New Password</label>
            <input
              type="password"
              className="form-control"
              {...formik.getFieldProps("confirmPassword")}
            />
            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
              <div className="text-danger">{formik.errors.confirmPassword}</div>
            )}
          </div>

          <div className="d-flex gap-2 justify-content-end">
            {loading ? (
              <LoadingButton name="Changing..." />
            ) : (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!formik.isValid || formik.isSubmitting}
              >
                Change Password
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
